<template>
  <div>
    <Head title="Cuentas" />

    <div class="max-w-7xl mx-auto py-10 sm:px-6 lg:px-8">
      <div class="flex justify-between items-center mb-8">
        <h1 class="text-3xl font-bold">Cuentas</h1>
      </div>

      <div class="bg-white shadow overflow-hidden sm:rounded-lg">
        <div class="px-4 py-5 sm:px-6">
          <div class="flex flex-col gap-4">
            <div>
              <h3 class="text-lg leading-6 font-medium text-gray-900">
                Listado de Cuentas
              </h3>
            </div>
            
            <div class="flex flex-wrap items-center gap-3 bg-gray-50/50 p-3 rounded-lg border border-gray-100">
              <input
                v-model="form.search"
                type="text"
                placeholder="Buscar (ID, nombre, email)..."
                class="block w-full sm:w-auto flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
              />
              <select
                v-model="form.plan"
                class="block w-full sm:w-auto rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
              >
                <option value="">Todos los planes</option>
                <option value="free">Free</option>
                <option value="entrepreneur">Entrepreneur</option>
                <option value="sme">SME</option>
                <option value="enterprise">Enterprise</option>
              </select>
              <select
                v-model="form.status"
                class="block w-full sm:w-auto rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
              >
                <option value="">Todos los estados</option>
                <option value="activo">Activo</option>
                <option value="suspendido">Suspendido</option>
                <option value="prueba">Prueba</option>
                <option value="cancelado">Cancelado</option>
              </select>
              <div class="flex items-center gap-2">
                <input
                  v-model="form.date_from"
                  type="date"
                  class="block rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                />
                <span class="text-gray-400 text-sm">-</span>
                <input
                  v-model="form.date_to"
                  type="date"
                  class="block rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                />
              </div>
              <button
                type="button"
                class="ml-auto inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded-md transition-colors"
                title="Limpiar filtros"
                @click="resetFilters"
              >
                <i class="fas fa-times-circle mr-1.5" /> Limpiar
              </button>
            </div>
          </div>
        </div>

        <div class="border-t border-gray-200 overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cuenta/Empresa</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email (Principal)</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usuarios</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Registro</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="account in accounts.data" :key="account.id">
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm font-medium text-gray-900">{{ account.id }}</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm font-medium text-gray-900">{{ account.name }}</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm text-gray-500">{{ account.email }}</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span 
                    :class="{
                      'bg-gray-100 text-gray-800': account.plan === 'free',
                      'bg-blue-100 text-blue-800': account.plan === 'entrepreneur',
                      'bg-purple-100 text-purple-800': account.plan === 'sme',
                      'bg-green-100 text-green-800': account.plan === 'enterprise'
                    }"
                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                  >
                    {{ getPlanName(account.plan) }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm text-gray-500">{{ account.users ? account.users.length : 0 }}</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm text-gray-500">{{ formatDate(account.created_at) }}</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div class="flex space-x-4">
                    <Link 
                      :href="`/admin/accounts/${account.id}`" 
                      class="text-indigo-600 hover:text-indigo-900"
                    >
                      Ver
                    </Link>
                    <Link 
                      :href="`/admin/accounts/${account.id}/edit`" 
                      class="text-indigo-600 hover:text-indigo-900"
                    >
                      Editar
                    </Link>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <div class="px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
            <div class="flex items-center justify-between">
              <div class="flex-1 flex justify-between sm:hidden">
                <Link v-if="accounts.prev_page_url" :href="accounts.prev_page_url" class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"> Anterior </Link>
                <Link v-if="accounts.next_page_url" :href="accounts.next_page_url" class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"> Siguiente </Link>
              </div>
              <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p class="text-sm text-gray-700">
                    Mostrando <span class="font-medium">{{ accounts.from || 0 }}</span> a <span class="font-medium">{{ accounts.to || 0 }}</span> de <span class="font-medium">{{ accounts.total }}</span> resultados
                  </p>
                </div>
                <div>
                  <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <template v-for="(link, key) in accounts.links">
                      <div v-if="link.url === null" :key="key" class="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 cursor-not-allowed" v-html="link.label" />
                      <Link v-else :key="'link-'+key" :href="link.url" :class="[link.active ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50', 'relative inline-flex items-center px-4 py-2 border text-sm font-medium']" v-html="link.label" />
                    </template>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import { Inertia } from '@inertiajs/inertia'
import Layout from '@/Shared/Layout'

export default {
  name: 'AccountsIndex',

  components: {
    Head,
    Link,
  },

  layout: Layout,

  props: {
    accounts: Object,
    filters: Object,
  },

  data() {
    return {
      form: {
        search: this.filters.search || '',
        plan: this.filters.plan || '',
        status: this.filters.status || '',
        date_from: this.filters.date_from || '',
        date_to: this.filters.date_to || '',
      },
      debounceTimer: null,
    }
  },

  watch: {
    form: {
      deep: true,
      handler(value) {
        clearTimeout(this.debounceTimer)
        this.debounceTimer = setTimeout(() => {
          Inertia.get('/admin/accounts', value, {
            preserveState: true,
            replace: true,
          })
        }, 300)
      },
    },
  },

  methods: {
    resetFilters() {
      this.form.search = ''
      this.form.plan = ''
      this.form.status = ''
      this.form.date_from = ''
      this.form.date_to = ''
      clearTimeout(this.debounceTimer)
      Inertia.get('/admin/accounts', {}, {
        preserveState: true,
        replace: true,
      })
    },

    getPlanName(plan) {
      const plans = {
        free: 'Free',
        entrepreneur: 'Entrepreneur',
        sme: 'SME',
        enterprise: 'Enterprise',
      }
      return plans[plan] || plan
    },

    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleDateString()
    },
  },
}
</script>